import { defineVariants } from "@/runtime/style-engine/define-variants";

/**
 * Tailwind-like class variants for the `<e-switch>` root container.
 */
export const switchRootClasses = defineVariants({
  base: `
    e-switch
    group
    inline-grid grid-cols-[auto_1fr] gap-x-2 gap-y-1
  `,

  variants: {
    disabled: {
      true: `opacity-50 cursor-not-allowed`,
      false: "cursor-pointer",
    },
    encased: {
      true: "grid w-full p-3 cursor-pointer",
      false: "",
    },
  },

  defaults: {
    disabled: false,
    encased: false,
  },
});

/**
 * Tailwind-like class variants for the switch switch background track.
 */
export const switchTrackClasses = defineVariants({
  base: `
    e-switch-track
    relative
    rounded-full
    box-border
    cursor-pointer
    outline-none
    transition-colors
    focus-visible:ring-2 focus-visible:ring-(--color-foreground)
    shrink-0
  `,

  variants: {
    color: {
      primary: `[--switch-color:var(--color-primary)]`,
      secondary: `[--switch-color:var(--color-secondary)]`,
      accent: `[--switch-color:var(--color-accent)]`,
      error: `[--switch-color:var(--color-error)]`,
      warning: `[--switch-color:var(--color-warning)]`,
      info: `[--switch-color:var(--color-info)]`,
      success: `[--switch-color:var(--color-success)]`,
      neutral: `[--switch-color:var(--color-neutral)]`,
      mono: `[--switch-color:var(--color-foreground)]`,
    },

    variant: {
      solid: "",
      outline: "border-(--switch-color)",
      soft: "",
      subtle: "",
    },

    size: {
      xs: `h-3.5 w-5.5 my-1.5`,
      sm: `h-4 w-6.5 my-1`,
      md: `h-4.5 w-7.5 my-1`,
      lg: `h-5 w-8.5 my-0.5`,
      xl: `h-5.5 w-9.5 my-0.5`,
    },

    disabled: {
      true: `opacity-50 cursor-not-allowed!`,
      false: "",
    },

    checked: {
      true: "",
      false: "",
    },
  },

  defaults: {
    color: "neutral",
    variant: "subtle",
    size: "md",
    disabled: false,
    checked: false,
  },

  compoundVariants: [
    // Solid variant
    {
      variant: "solid",
      checked: "false",
      class: "bg-(--switch-color)/50",
    },
    {
      variant: "solid",
      checked: "true",
      class: "bg-(--switch-color)",
    },
    // Outline variant
    {
      variant: "outline",
      checked: "true",
      class: "bg-(--switch-color)",
    },
    // Outline + neutral: use darker elevation color to stand out from white card backgrounds
    {
      variant: "outline",
      checked: "true",
      color: "neutral",
      class: "bg-(--color-elevation-600)",
    },
    // Soft variant
    {
      variant: "soft",
      checked: "false",
      class: "bg-(--switch-color)/15",
    },
    {
      variant: "soft",
      checked: "true",
      class: "bg-(--switch-color)/35",
    },
    // Subtle variant
    {
      variant: "subtle",
      checked: "false",
      class: "bg-(--switch-color)/15 border-(--switch-color)/35",
    },
    {
      variant: "subtle",
      checked: "true",
      class: "bg-(--switch-color)/35 border-(--switch-color)/50",
    },
    {
      variant: ["subtle", "outline"],
      class: "border",
    },
    {
      color: "mono",
      checked: "true",
      variant: "*",
      class: "bg-(--switch-color)!",
    },
    // Size adjustments for variants without borders (soft, solid)
    // These are 2px smaller in each dimension (no 1px border on each side)
    {
      variant: ["soft", "solid"],
      size: "xs",
      class: "h-3 w-5",
    },
    {
      variant: ["soft", "solid"],
      size: "sm",
      class: "h-3.5 w-6",
    },
    {
      variant: ["soft", "solid"],
      size: "md",
      class: "h-4 w-7",
    },
    {
      variant: ["soft", "solid"],
      size: "lg",
      class: "h-4.5 w-8",
    },
    {
      variant: ["soft", "solid"],
      size: "xl",
      class: "h-5 w-9",
    },
  ],
});

/**
 * Tailwind-like class variants for the switch thumb (the moving circle).
 */
export const switchThumbClasses = defineVariants({
  base: `
    e-switch-thumb
    absolute
    rounded-full
    transition-all
    pointer-events-none
    flex items-center justify-center
  `,

  variants: {
    color: {
      primary: `bg-(--color-background)`,
      secondary: `bg-(--color-background)`,
      accent: `bg-(--color-background)`,
      error: `bg-(--color-background)`,
      warning: `bg-(--color-background)`,
      info: `bg-(--color-background)`,
      success: `bg-(--color-background)`,
      neutral: `bg-(--color-background)`,
      mono: `bg-(--color-background)`,
    },

    variant: {
      solid: "",
      outline: "",
      soft: "",
      subtle: "",
    },

    size: {
      xs: `size-2 top-0.5 left-0.5`,
      sm: `size-2.5 top-0.5 left-0.5`,
      md: `size-3 top-0.5 left-0.5`,
      lg: `size-3.5 top-0.5 left-0.5`,
      xl: `size-4 top-0.5 left-0.5`,
    },

    checked: {
      true: "",
      false: "",
    },

    disabled: {
      true: "",
      false: "",
    },
  },

  defaults: {
    color: "neutral",
    variant: "subtle",
    size: "md",
    checked: false,
    disabled: false,
  },

  compoundVariants: [
    {
      size: "xs",
      checked: "true",
      class: "translate-x-2",
    },
    {
      size: "sm",
      checked: "true",
      class: "translate-x-2.5",
    },
    {
      size: "md",
      checked: "true",
      class: "translate-x-3",
    },
    {
      size: "lg",
      checked: "true",
      class: "translate-x-3.5",
    },
    {
      size: "xl",
      checked: "true",
      class: "translate-x-4",
    },
    {
      variant: ["soft", "solid"],
      size: "*",
      class: "top-0.75",
    },
    {
      variant: ["soft", "solid"],
      size: "xs",
      checked: "true",
      class: "translate-x-2.5",
    },
    {
      variant: ["soft", "solid"],
      size: "sm",
      checked: "true",
      class: "translate-x-3",
    },
    {
      variant: ["soft", "solid"],
      size: "md",
      checked: "true",
      class: "translate-x-3.5",
    },
    {
      variant: ["soft", "solid"],
      size: "lg",
      checked: "true",
      class: "translate-x-4",
    },
    {
      variant: ["soft", "solid"],
      size: "xl",
      checked: "true",
      class: "translate-x-4.5",
    },
    {
      checked: "true",
      class: "bg-(--color-white)",
    },
    {
      variant: "outline",
      checked: "false",
      class: "bg-(--switch-color)",
    },
    {
      variant: "outline",
      checked: "false",
      color: "neutral",
      class: "bg-(--color-elevation-600)",
    },
    {
      variant: "solid",
      checked: "false",
      class: "bg-(--switch-color)/50",
    },
    {
      variant: "soft",
      checked: "false",
      class: "bg-(--switch-color)/50",
    },
    {
      variant: "soft",
      checked: "true",
      color: "mono",
      class: "bg-(--switch-color)",
    },
    {
      variant: "subtle",
      checked: "false",
      class: "bg-(--switch-color)/50",
    },
    {
      variant: "subtle",
      checked: "true",
      color: "mono",
      class: "bg-(--switch-color)",
    },
    {
      color: "mono",
      variant: "*",
      checked: "*",
      class: "bg-(--color-background)!",
    },
    {
      color: "mono",
      variant: "outline",
      checked: "false",
      class: "bg-(--color-foreground)/20!",
    },
    {
      checked: "false",
      disabled: "false",
      class: "group-hover:scale-x-125 group-hover:origin-left",
    },
    {
      checked: "true",
      disabled: "false",
      class: "group-hover:scale-x-125 group-hover:origin-right",
    },
    {
      disabled: "true",
      class: "group-hover:scale-x-100",
    },
  ],
});
